import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import FormButton from "../../Components/Buttons/FormButton";
import FormSelect from "../../Components/FormTag/FormSelect";
import InputFiled from "../../Components/FormTag/InputFiled";
import { FormValidation } from "../../Utility/FormValidations";
import { formatInputData, transformLookupToSelectOptions } from "../../Utility/Utility";
import { AddressSectionLookUp } from "./model/accountForm.model";
import { CompanyAddress } from "./model/CompanyAccount.type";
import Loader from "../../Components/GlobalLoader/Loader";

interface AddressProps {
  handleNext?: any;
  compAddress?: CompanyAddress;
  setCompAddress?: (data: CompanyAddress) => void;
  addressLookUpData?: AddressSectionLookUp;
}

export default function Address(props: AddressProps) {
  const navigator = useNavigate();
  const { handleNext, setCompAddress, compAddress, addressLookUpData } = props;
  const [loading, setLoading] = useState(true);
  const [lookupOptions, setLookupOptions] = useState<{
    timezoneOptions: { label: string; value: string }[];
    countryOptions: { label: string; value: string }[];
  }>({
    timezoneOptions: [],
    countryOptions: [],
  });

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<CompanyAddress>({
    defaultValues: compAddress,
    mode: "onChange",
  });

  useEffect(() => {
    if (addressLookUpData) {
      const timezoneOptions = transformLookupToSelectOptions(
        addressLookUpData?.timezone,
        { labelField: "name", valueField: "name" }
      );

      const countryOptions = transformLookupToSelectOptions(
        addressLookUpData?.countries,
        { labelField: "name", valueField: "code" }
      );
      setLookupOptions({
        timezoneOptions,
        countryOptions,
      });
    }
  }, [addressLookUpData]);

  useEffect(() => {
    if (
      addressLookUpData &&
      compAddress &&
      lookupOptions.timezoneOptions.length > 0
    ) {
      setLoading(false);
    }
  }, [compAddress, addressLookUpData, lookupOptions]);

  useEffect(() => {
    if (compAddress) {
      reset(compAddress);
    }
  }, [compAddress, reset]);

  const saveItemsDetails: SubmitHandler<CompanyAddress> = (data) => {
    if (setCompAddress) {

      setCompAddress({
        ...data,
        country: formatInputData(data.country),
      });
    }
    handleNext();
  };

  const handelCancelBtn = () => {
    navigator("/account/company-account/grid-view");
  };

  return loading ? (
    <Loader />
  ) : (
    <form>
      {/* 1st row */}
      <div className="col-12 company-account-address">
        <div className="my-2 bg-white rounded p-3">
          <div className="d-flex px-2 gap-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Company Name *</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={1}
                  required={true}
                  type="text"
                  register={register}
                  name="contact_name"
                  errors={errors.contact_name}
                  className="form-control"
                  placeholder="Enter Company Name"
                />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">Time Zone *</label>
              <div className="mt-2">
                <FormSelect
                  name="time_zone_name"
                  errors={errors.time_zone_name}
                  required={true}
                  tabIndex={2}
                  control={control}
                  optionList={lookupOptions.timezoneOptions}
                  className="form-control p-0"
                  placeholder="Select"
                  isSearchable={true}
                  defaultValue={compAddress?.time_zone_name}
                />
              </div>
            </div>
          </div>
          {/* 2rd row */}
          <div className="d-flex px-2 gap-3 mt-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Address 1</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={3}
                  type="text"
                  register={register}
                  name="address_line_1"
                  errors={errors.address_line_1}
                  className="form-control"
                  placeholder="Enter Address 1"
                />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">Address 2</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={4}
                  type="text"
                  register={register}
                  name="address_line_2"
                  errors={errors.address_line_2}
                  className="form-control"
                  placeholder="Enter Address 2"
                />
              </div>
            </div>
          </div>
          {/* 3rd row*/}
          <div className="d-flex px-2 gap-3 mt-3">
            <div className="col-4 px-2">
              <label className="fw-medium">City</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={5}
                  type="text"
                  register={register}
                  name="city"
                  errors={errors.city}
                  className="form-control"
                  placeholder="Enter City"
                />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">State / Province</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={6}
                  type="text"
                  register={register}
                  name="state_province"
                  errors={errors.state_province}
                  className="form-control"
                  placeholder="Enter State / Province"
                />
              </div>
            </div>
          </div>

          {/* 4th row */}
          <div className="d-flex px-2 gap-3 mt-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Zip Code / Postal Code</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={7}
                  type="text"
                  register={register}
                  name="postal_code"
                  errors={errors.postal_code}
                  className="form-control"
                  placeholder="Enter Zip Code / Postal Code"
                  regex={FormValidation.numeric}
                />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">Country</label>
              <div className="mt-1">
                <FormSelect
                  name="country"
                  tabIndex={8}
                  control={control}
                  optionList={lookupOptions.countryOptions}
                  className="form-control p-0"
                  placeholder="Select"
                  isSearchable={true}
                  defaultValue={compAddress?.country}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="my-2 bg-white rounded py-3">
        <div className="d-flex flex-row-reverse">
          <FormButton
            tabIndex={9}
            className="sph-btn me-4"
            handleClick={handleSubmit(saveItemsDetails)}
          >
            Next
          </FormButton>
          <FormButton
            tabIndex={10}
            className="sph-btn sph-shift me-4"
            handleClick={handelCancelBtn}
          >
            Cancel
          </FormButton>
        </div>
      </div>
    </form>
  );
}
